import { useEffect } from 'react'

import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverAction,
  PopoverContent,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from '@/components/ui/popover'
import { PencilIcon } from 'lucide-react'

import { NodeViewWrapper } from '@tiptap/react'

import { MentionNodeAttributes } from '~shared/richtext/nodes/mention.node'
import { RundownPermission } from '~shared/rundown/permission'

import { useMention, type MentionDto } from '@/entities/mentions'

import { useRundownPermission } from '@/features/permissions/lib/use-rundown-permission'
import type {
  NodeComponentProps,
  NodeViewLiveProps,
  NodeViewStaticProps,
} from '@/features/richtext/tiptap.helpers'
import { MentionDescPreview } from '@/features/rundown/mentions/MentionDescPreview'
import { modalActions } from '@/features/rundown/modals/modal.store'
import { useRundownId } from '@/features/rundown/rundown.derived'

import { MentionChip, MentionChipLoading } from './MentionChip'

import type { NodeViewProps, ReactNodeViewProps } from '@tiptap/react'

export function MentionNodeViewLive({
  node,
  updateAttributes,
  ...props
}: NodeViewLiveProps) {
  const attrs = node.attrs as MentionNodeAttributes
  const rundownId = useRundownId()

  const { data: mention } = useMention({
    params: {
      rundownId,
      mentionId: attrs.mentionId,
    },
  })

  useEffect(() => {
    if (mention) {
      updateAttributes({
        fallbackName: mention.name,
      })
    }
  }, [mention, updateAttributes])

  return (
    <MentionNodeComponent
      node={node}
      HTMLAttributes={props.HTMLAttributes}
    />
  )
}

export function MentionNodeViewStatic({ node }: NodeViewStaticProps) {
  return <MentionNodeComponent node={node} />
}

function MentionNodeComponent({
  node,
  HTMLAttributes,
}: NodeComponentProps & {
  HTMLAttributes?: NodeViewProps['HTMLAttributes']
}) {
  const attrs = node.attrs as MentionNodeAttributes
  const rundownId = useRundownId()

  const {
    data: mention,
    isLoading,
    isSuccess,
    isError,
  } = useMention({
    params: {
      rundownId,
      mentionId: attrs.mentionId,
    },
  })

  const hasWriteAccess = useRundownPermission({
    permission: RundownPermission.EDIT,
  })

  if (isError || !attrs.mentionId || !isSuccess) {
    return (
      <MentionNodeLayout {...HTMLAttributes}>
        <span>@{attrs.fallbackName}</span>
      </MentionNodeLayout>
    )
  }

  if (isLoading) {
    return (
      <MentionNodeLayout {...HTMLAttributes}>
        <MentionChipLoading />
      </MentionNodeLayout>
    )
  }

  return (
    <MentionNodeLayout {...HTMLAttributes}>
      <Popover>
        <PopoverTrigger
          nativeButton={false}
          tabIndex={-1}
          render={
            <MentionChip
              mention={mention}
              className="cursor-help"
            />
          }
        />
        <PopoverContent
          initialFocus={false}
          className="w-80"
        >
          <PopoverHeader>
            <PopoverTitle>Mention</PopoverTitle>

            <PopoverAction>
              <Button
                aria-label="Edit mention"
                variant="ghost"
                size="icon-sm"
                disabled={!hasWriteAccess}
                onClick={() => modalActions.open('MENTIONS', {})}
              >
                <PencilIcon />
              </Button>
            </PopoverAction>
          </PopoverHeader>

          <div className="space-y-2">
            <MentionChip mention={mention} />

            <div className="max-h-[70vh] overflow-y-auto">
              <MentionPopoverDescription mention={mention} />
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </MentionNodeLayout>
  )
}

function MentionNodeLayout({
  children,
  ...HTMLAttributes
}: React.PropsWithChildren &
  ReactNodeViewProps<HTMLSpanElement>['HTMLAttributes']) {
  return (
    <NodeViewWrapper
      as="span"
      {...HTMLAttributes}
      className="inline-block align-baseline"
    >
      {children}
    </NodeViewWrapper>
  )
}

function MentionPopoverDescription({ mention }: { mention: MentionDto }) {
  if (!mention.description?.length || mention.description === '<p></p>') {
    return (
      <p className="px-2 py-1 text-sm italic opacity-50">
        No description added.
      </p>
    )
  }

  return <MentionDescPreview content={mention.description} />
}
